import ApiError from "@/api/ApiError";
import { AUTH_REDIRECTS } from "@/utils/auth-routes";
import { getServerCookies } from "@/utils/auth-server";

interface ApiArgs {
  basePath: string;
}
interface RequestOptions extends RequestInit {
  protected?: boolean;
}

const isBrowser = () => typeof window !== "undefined";
const isFormData = (value: unknown): value is FormData => typeof FormData !== "undefined" && value instanceof FormData;

const parseBody = async (response: Response) => {
  if (!response.headers.get("content-type")?.includes("application/json")) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
};

class Api {
  constructor(private readonly args: ApiArgs) {}

  async get<T>(path: string, params?: object, options?: RequestOptions): Promise<T> {
    const searchParams = new URLSearchParams();
    if (params)
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        if (Array.isArray(value)) value.forEach(item => searchParams.append(`${key}[]`, String(item)));
        else searchParams.append(key, String(value));
      });
    return this.request<T>(`${path}${searchParams.size ? `?${searchParams}` : ""}`, { ...options, method: "GET" });
  }

  async post<T>(path: string, data?: object | FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "POST",
      body: data ? (isFormData(data) ? data : JSON.stringify(data)) : undefined,
    });
  }

  async patch<T>(path: string, data?: object | FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PATCH",
      body: data ? (isFormData(data) ? data : JSON.stringify(data)) : undefined,
    });
  }

  async put<T>(path: string, data?: object | FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "PUT",
      body: data ? (isFormData(data) ? data : JSON.stringify(data)) : undefined,
    });
  }

  async delete<T>(path: string, data?: object | FormData, options?: RequestOptions): Promise<T> {
    return this.request<T>(path, {
      ...options,
      method: "DELETE",
      body: data ? (isFormData(data) ? data : JSON.stringify(data)) : undefined,
    });
  }

  private buildUrl(path: string): string {
    if (path.startsWith("http")) return path;
    if (!path.startsWith("/")) throw new Error(`[ApiClient] Path must start with "/": ${path}`);
    const base = isBrowser() ? "" : this.args.basePath;
    return `${base}/api${path}`;
  }

  private async request<T>(path: string, options?: RequestOptions): Promise<T> {
    const { protected: isProtected, ...fetchOptions } = options || {};
    const headers = new Headers(fetchOptions.headers);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (!isFormData(fetchOptions.body) && fetchOptions.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (!isBrowser() && isProtected) {
      const { session } = await getServerCookies();
      if (session) headers.set("Cookie", `session=${encodeURIComponent(session)}`);
    }

    const response = await fetch(this.buildUrl(path), {
      ...fetchOptions,
      headers,
      credentials: "include",
      cache: "no-store",
    });
    const body = await parseBody(response);
    if (!response.ok) {
      if (response.status === 401 && isProtected && isBrowser()) {
        window.location.replace(AUTH_REDIRECTS.unauthorized);
      }
      throw new ApiError(response.status, body?.message || body?.error || response.statusText || "Request failed");
    }
    return body as T;
  }
}

export default Api;
